import { useEffect, useRef, useState } from 'react';

const REDUCED_MOTION_TOTAL_MS = 300;

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/**
 * Reveals `stepCount` loading lines over `totalMs`, holds on the last line for one more beat,
 * then calls `onDone` once the data it was waiting for (`isReady`) has also arrived.
 */
export function useTimedSequence(
  stepCount: number,
  totalMs: number,
  isReady: boolean,
  onDone: () => void,
) {
  const [completed, setCompleted] = useState(0);
  const [heldLastLine, setHeldLastLine] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const stepMs = (prefersReducedMotion() ? REDUCED_MOTION_TOTAL_MS : totalMs) / stepCount;
    let count = 0;
    const timer = window.setInterval(() => {
      count += 1;
      if (count <= stepCount) {
        setCompleted(count);
        return;
      }
      setHeldLastLine(true);
      window.clearInterval(timer);
    }, stepMs);
    return () => window.clearInterval(timer);
  }, [stepCount, totalMs]);

  const isFinished = heldLastLine && isReady;

  useEffect(() => {
    if (isFinished) onDoneRef.current();
  }, [isFinished]);

  return { completed, progress: (completed / stepCount) * 100 };
}
